// Mobile Menu with inline accordion navigation
class MobileMenu {
  constructor(options = {}) {
    this.state = {
      mobileMenu: document.querySelector('[data-component="mobile-menu"]'),
      menuButton: document.querySelector('[data-action="open-menu"]'),
      closeButton: document.querySelector('[data-action="close-menu"]'),
      menuOverlay: document.querySelector('[data-component="menu-overlay"]'),
      menuNav: document.getElementById('mobile-menu-nav'),
      countryPanel: document.getElementById('mobile-country-panel'),
      isOpen: false,
      config: {
        openAttribute: options.openAttribute || 'data-menu-open',
        trapFocus: options.trapFocus !== false
      }
    };

    this.focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    this.init();
  }

  validateElements() {
    if (!this.state.mobileMenu || !this.state.menuButton) {
      console.error('Required menu elements not found');
      return false;
    }
    return true;
  }

  updateFocusableElements() {
    // #mobile-country-panel lives outside #mobile-menu in the DOM (see markup
    // comment) so it has to be unioned in separately for the focus trap.
    const roots = [this.state.mobileMenu, this.state.countryPanel].filter(Boolean);
    this.focusableElements = roots.flatMap((root) => Array.from(root.querySelectorAll(this.focusableSelectors)));
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  handleTabKey = (e) => {
    if (!this.state.config.trapFocus || !this.state.isOpen) return;

    const isTabPressed = e.key === 'Tab';
    if (!isTabPressed) return;

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement?.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement?.focus();
      }
    }
  }

  handleClickOutside = (e) => {
    if (
      this.state.isOpen &&
      !this.state.mobileMenu.contains(e.target) &&
      !this.state.menuButton.contains(e.target) &&
      !this.state.countryPanel?.contains(e.target)
    ) {
      this.close();
    }
  }

  handleOverlayClick = (e) => {
    if (e.target === this.state.menuOverlay) {
      this.close();
    }
  }

  handleEscapeKey = (e) => {
    if (e.key !== 'Escape' || !this.state.isOpen) return;

    if (this.state.countryPanel?.classList.contains('active')) {
      this.closeCountryPanel();
      return;
    }

    const openItems = this.state.mobileMenu.querySelectorAll('.mobile-accordion-item.is-open');
    if (openItems.length) {
      // Deepest-nested open item is last in document order
      this.closeItem(openItems[openItems.length - 1]);
    } else {
      this.close();
    }
  }

  handleMenuButtonClick = (e) => {
    e.preventDefault();
    this.state.isOpen ? this.close() : this.open();
  }

  handleCloseButtonClick = (e) => {
    e.preventDefault();
    this.close();
  }

  handleAccordionToggle = (e) => {
    const trigger = e.target.closest('[data-action="toggle-accordion"]');
    if (!trigger) return;

    const item = trigger.closest('.mobile-accordion-item');
    if (!item) return;

    e.preventDefault();
    this.toggleItem(item);
  }

  closeItem(item) {
    item.classList.remove('is-open');
    const trigger = item.querySelector(':scope > .mobile-accordion-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    // Close any nested open items and drop their parent lists' active state
    item.querySelectorAll('.mobile-accordion-item.is-open').forEach((nested) => {
      nested.classList.remove('is-open');
      const nestedTrigger = nested.querySelector(':scope > .mobile-accordion-trigger');
      if (nestedTrigger) nestedTrigger.setAttribute('aria-expanded', 'false');
    });
    item.querySelectorAll('.mobile-accordion-list').forEach((list) => {
      list.classList.remove('has-open-child');
    });
  }

  openItem(item) {
    const parentList = item.parentElement;
    if (!parentList) return;

    // Only one open item per level — close any other open sibling first
    Array.from(parentList.children).forEach((sibling) => {
      if (sibling !== item && sibling.classList.contains('is-open')) {
        this.closeItem(sibling);
      }
    });

    item.classList.add('is-open');
    parentList.classList.add('has-open-child');
    const trigger = item.querySelector(':scope > .mobile-accordion-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  toggleItem(item) {
    if (item.classList.contains('is-open')) {
      this.closeItem(item);
      const parentList = item.parentElement;
      if (parentList && !parentList.querySelector(':scope > .mobile-accordion-item.is-open')) {
        parentList.classList.remove('has-open-child');
      }
    } else {
      this.openItem(item);
    }
    this.updateFocusableElements();
  }

  handleOpenCountryPanel = (e) => {
    const trigger = e.target.closest('[data-action="open-country-panel"]');
    if (!trigger) return;

    e.preventDefault();
    this.openCountryPanel();
  }

  handleCloseCountryPanel = (e) => {
    const trigger = e.target.closest('[data-action="close-country-panel"]');
    if (!trigger) return;

    e.preventDefault();
    this.closeCountryPanel();
  }

  openCountryPanel() {
    if (!this.state.countryPanel) return;

    this.state.countryPanel.classList.add('active');
    this.state.countryPanel.setAttribute('aria-hidden', 'false');
    this.state.countryPanel.inert = false;
    // Whole menu, not just the nav: the country/currency trigger is a footer
    // row outside #mobile-menu-nav, and the close button sits behind the panel.
    this.state.mobileMenu.inert = true;

    document.querySelector('[data-action="open-country-panel"]')?.setAttribute('aria-expanded', 'true');

    this.updateFocusableElements();
    // Focusing while the panel is still translating off-screen can make Safari
    // snap-scroll to it mid-transition — wait for the slide-in to finish.
    setTimeout(() => {
      this.state.countryPanel.querySelector('[data-action="close-country-panel"]')?.focus();
    }, 300);
  }

  closeCountryPanel() {
    if (!this.state.countryPanel || !this.state.countryPanel.classList.contains('active')) return;

    this.state.countryPanel.classList.remove('active');
    this.state.countryPanel.setAttribute('aria-hidden', 'true');
    this.state.countryPanel.inert = true;
    this.state.mobileMenu.inert = false;

    const pickerTrigger = document.querySelector('[data-action="open-country-panel"]');
    pickerTrigger?.setAttribute('aria-expanded', 'false');
    pickerTrigger?.focus();

    this.updateFocusableElements();
  }

  resetAccordion() {
    if (!this.state.menuNav) return;
    this.state.menuNav.querySelectorAll('.mobile-accordion-item.is-open').forEach((item) => {
      item.classList.remove('is-open');
      const trigger = item.querySelector(':scope > .mobile-accordion-trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
    this.state.menuNav.querySelectorAll('.mobile-accordion-list.has-open-child').forEach((list) => {
      list.classList.remove('has-open-child');
    });
  }

  open() {
    if (this.state.isOpen) return;

    this.state.mobileMenu.classList.add('active');
    this.state.mobileMenu.inert = false;
    document.body.style.overflow = 'hidden';
    this.state.menuButton?.setAttribute(this.state.config.openAttribute, 'true');
    this.state.menuButton?.setAttribute('aria-expanded', 'true');
    this.state.mobileMenu.setAttribute('aria-hidden', 'false');

    // Add mobile-menu-open class to header for logo color change
    const header = document.getElementById('site-header');
    if (header) {
      header.classList.add('mobile-menu-open');
    }
    document.body.classList.add('mobile-menu-open');

    // Show the overlay
    if (this.state.menuOverlay) {
      this.state.menuOverlay.classList.add('active');
      this.state.menuOverlay.setAttribute('aria-hidden', 'false');
    }

    // Update focusable elements before focusing
    this.updateFocusableElements();
    this.firstFocusableElement?.focus();

    this.state.isOpen = true;

    // Dispatch custom event
    this.state.mobileMenu.dispatchEvent(new CustomEvent('menu:opened', {
      bubbles: true
    }));
  }

  close() {
    if (!this.state.isOpen) return;

    this.state.mobileMenu.classList.remove('active');
    document.body.style.overflow = '';
    this.state.menuButton?.setAttribute(this.state.config.openAttribute, 'false');
    this.state.menuButton?.setAttribute('aria-expanded', 'false');
    this.state.mobileMenu.setAttribute('aria-hidden', 'true');

    // Hide the overlay
    if (this.state.menuOverlay) {
      this.state.menuOverlay.classList.remove('active');
      this.state.menuOverlay.setAttribute('aria-hidden', 'true');
    }

    // Clear search inputs and hide autocomplete
    this.state.mobileMenu.querySelectorAll('[data-search-input]').forEach(input => {
      input.value = '';
      const dropdown = input.closest('[data-search-form]')?.querySelector('[data-search-autocomplete]');
      if (dropdown) {
        dropdown.classList.remove('is-visible');
        dropdown.innerHTML = '';
      }
    });

    this.state.menuButton?.focus();
    this.state.isOpen = false;

    // Everything reverts in the same frame as the panel: closing is instant
    // (see .mobile-menu in animations.css), so a delayed reset here would leave the
    // logo black over the dark hero, or slide the country panel back out across the
    // page the menu just uncovered.
    const header = document.getElementById('site-header');
    if (header) {
      header.classList.remove('mobile-menu-open');
    }
    document.body.classList.remove('mobile-menu-open');

    this.resetAccordion();
    if (this.state.countryPanel) {
      // Suppress the slide-out: the panel is being hidden with the menu, not navigated away from
      this.state.countryPanel.classList.add('no-transition');
      this.state.countryPanel.classList.remove('active');
      void this.state.countryPanel.offsetHeight; // flush the transform before re-enabling transitions
      this.state.countryPanel.classList.remove('no-transition');
      this.state.countryPanel.setAttribute('aria-hidden', 'true');
      this.state.countryPanel.inert = true;
      this.state.mobileMenu.inert = false;
      document.querySelector('[data-action="open-country-panel"]')?.setAttribute('aria-expanded', 'false');
    }

    // Dispatch custom event
    this.state.mobileMenu.dispatchEvent(new CustomEvent('menu:closed', {
      bubbles: true
    }));
  }

  bindEvents() {
    this.state.menuButton?.addEventListener('click', this.handleMenuButtonClick);
    this.state.closeButton?.addEventListener('click', this.handleCloseButtonClick);

    document.addEventListener('click', this.handleAccordionToggle);
    document.addEventListener('click', this.handleOpenCountryPanel);
    document.addEventListener('click', this.handleCloseCountryPanel);
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleEscapeKey);

    // Add overlay click handler
    this.state.menuOverlay?.addEventListener('click', this.handleOverlayClick);

    if (this.state.config.trapFocus) {
      document.addEventListener('keydown', this.handleTabKey);
    }
  }

  unbindEvents() {
    this.state.menuButton?.removeEventListener('click', this.handleMenuButtonClick);
    this.state.closeButton?.removeEventListener('click', this.handleCloseButtonClick);

    document.removeEventListener('click', this.handleAccordionToggle);
    document.removeEventListener('click', this.handleOpenCountryPanel);
    document.removeEventListener('click', this.handleCloseCountryPanel);
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleEscapeKey);

    // Remove overlay click handler
    this.state.menuOverlay?.removeEventListener('click', this.handleOverlayClick);

    if (this.state.config.trapFocus) {
      document.removeEventListener('keydown', this.handleTabKey);
    }
  }

  init() {
    if (!this.validateElements()) return;

    // Set initial ARIA attributes
    this.state.menuButton?.setAttribute('aria-expanded', 'false');
    this.state.menuButton?.setAttribute('aria-controls', this.state.mobileMenu.id || 'mobile-menu');
    this.state.mobileMenu.setAttribute('aria-hidden', 'true');
    this.state.mobileMenu.setAttribute('role', 'navigation');

    // Country panel starts off-screen — keep it out of tab order until opened
    if (this.state.countryPanel) this.state.countryPanel.inert = true;

    this.bindEvents();
  }

  destroy() {
    if (this.state.isOpen) {
      this.close();
    }
    this.unbindEvents();
  }

  // Public API
  isOpen() {
    return this.state.isOpen;
  }
}

// Initialize mobile menu when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.mobileMenu = new MobileMenu();
});
